// src/pages/compliance/ToolTab.jsx
//
// Generic panel for every SOW-based compliance tool (SMP, RAID, TVR,
// Obligations, STM, RTM). CompliancePage.jsx used to repeat this same
// upload-or-reuse / progress / result structure once per tab, as a chain of
// `effectiveTab === TAB.X && <XUploadCard .../>` conditionals -- one
// component, parameterized by props, replaces all of them. CompliancePage
// still owns the state (featureState, sharedSowFile) and the handlers
// (onSubmit, onReuseSubmit, ...); this file only renders.
import PropTypes from 'prop-types'
import SowSourceBar from './SowSourceBar'
import ProgressCard from '../../components/compliance/ProgressCard'
import ResultCard from '../../components/compliance/ResultCard'

export default function ToolTab({
  tabKey,
  title,
  sub,
  UploadComponent,
  onSubmit,
  showSowBar = true,
  sharedSowFile = null,
  sharedSowUploadedAt = null,
  onReplaceSow,
  reuseLabel,
  onReuseSubmit,
  phase,
  jobId = null,
  jobData = null,
  error = null,
  onComplete,
  onFail,
  onReset,
}) {
  const hasSharedSow = showSowBar && Boolean(sharedSowFile)

  return (
    <>
      <div>
        <p className="idp-page-title">{title}</p>
        <p className="idp-page-sub">{sub}</p>
      </div>

      {phase === 'upload' && hasSharedSow && (
        <div className="c-card">
          <SowSourceBar
            file={sharedSowFile}
            uploadedAt={sharedSowUploadedAt}
            onReplace={onReplaceSow}
          />
          <button type="button" className="c-btn-primary" onClick={onReuseSubmit}>
            {reuseLabel}
          </button>
        </div>
      )}
      {phase === 'upload' && !hasSharedSow && <UploadComponent onSubmit={onSubmit} />}
      {phase === 'progress' && (
        <ProgressCard jobId={jobId} jobType={tabKey} onComplete={onComplete} onFail={onFail} />
      )}
      {phase === 'result' && (
        <ResultCard jobId={jobId} jobData={jobData} jobType={tabKey} error={error} onReset={onReset} />
      )}
    </>
  )
}

ToolTab.propTypes = {
  tabKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  sub: PropTypes.string.isRequired,
  UploadComponent: PropTypes.elementType.isRequired,
  onSubmit: PropTypes.func.isRequired,
  showSowBar: PropTypes.bool,
  sharedSowFile: PropTypes.instanceOf(File),
  sharedSowUploadedAt: PropTypes.number,
  onReplaceSow: PropTypes.func.isRequired,
  reuseLabel: PropTypes.string,
  onReuseSubmit: PropTypes.func.isRequired,
  phase: PropTypes.oneOf(['upload', 'progress', 'result']).isRequired,
  jobId: PropTypes.string,
  jobData: PropTypes.object,
  error: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
  onFail: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
}
